import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { FaDuotoneIconComponent } from '@fortawesome/angular-fontawesome';
import { faDownload, faLock, faSpinnerThird } from '@fortawesome/pro-duotone-svg-icons';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

import { Message } from '../../../state/messages.repository';
import { SquareIconBtn } from '../../../shared/components/ui/buttons/square-icon-btn/square-icon-btn';
import { Button } from '../../../shared/components/ui/buttons/gradient-btn/button';
import { RelativeTimePipe } from '../../../shared/pipes/relative-time.pipe';
import { fileTypeIcon } from '../../../shared/helpers/file-type-icon';
import { formatBytes } from '../../../shared/helpers/upload-image';
import { RoomFilesService } from '../room-files.service';
import { RoomFileDownloadController } from '../room-file-download';
import { LanguageService } from '../../../core/services/language.service';

/**
 * Chat row for a file shared in the room: who shared it, the file's type
 * icon, name and size, and a download button. Styled like a system notice
 * (no bubble). The card only carries the announcement — the file is looked up
 * in the room's live file list, so an expired or deleted file shows as no
 * longer available.
 */
@Component({
  selector: 'andor-room-file-card',
  standalone: true,
  imports: [FaDuotoneIconComponent, TranslatePipe, SquareIconBtn, Button, RelativeTimePipe],
  templateUrl: './room-file-card.html',
  styleUrl: './room-file-card.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [RoomFileDownloadController],
})
export class RoomFileCard {
  readonly message = input.required<Message>();

  protected readonly filesService = inject(RoomFilesService);
  protected readonly downloads = inject(RoomFileDownloadController);
  private readonly _translate = inject(TranslateService);
  // Read before `instant()` so labels re-translate when the language changes.
  protected readonly lang = inject(LanguageService).active;

  protected readonly faDownload = faDownload;
  protected readonly faLock = faLock;
  protected readonly faSpinnerThird = faSpinnerThird;
  protected readonly formatBytes = formatBytes;

  protected readonly payload = computed(() => {
    const m = this.message();
    return m.kind === 'file' ? m.payload : null;
  });

  protected readonly icon = computed(() => {
    const payload = this.payload();
    return fileTypeIcon(payload?.name ?? '', payload?.mime ?? '');
  });

  /** The shared file while it still exists in the room. */
  protected readonly file = computed(() => {
    const payload = this.payload();
    if (!payload) return undefined;
    // Track the list so the card follows additions and removals.
    this.filesService.files();
    return this.filesService.find(payload.fileId);
  });

  protected readonly headline = computed(() => {
    this.lang();
    const m = this.message();
    return m.origin === 'self'
      ? this._translate.instant('You shared a file')
      : this._translate.instant('[name] shared a file', { name: m.sender.name });
  });
}
